import { create } from 'zustand'
import type { Message, Conversation } from '../types'
import { api, makeStreamingRequest } from '../services/api'

interface ChatState {
  // Conversations
  conversations: Conversation[]
  currentConversation: Conversation | null
  isLoadingConversations: boolean

  // Messages
  messages: Message[]
  isStreaming: boolean
  streamingContent: string

  // UI State
  chatMode: 'assistant' | 'agentic' | 'multi-agent'
  selectedProvider: string
  isSidebarOpen: boolean
  searchQuery: string

  // Actions
  fetchConversations: (userId: string) => Promise<void>
  createConversation: (userId: string, title: string, mode: 'assistant' | 'agentic' | 'multi-agent') => Promise<Conversation>
  selectConversation: (conversation: Conversation) => void
  deleteConversation: (id: string) => Promise<void>
  
  sendMessage: (userId: string, content: string) => Promise<void>
  cancelStreaming: () => void
  
  setChatMode: (mode: 'assistant' | 'agentic' | 'multi-agent') => void
  setSelectedProvider: (provider: string) => void
  toggleSidebar: () => void
  setSearchQuery: (query: string) => void
  clearCurrentConversation: () => void
}

export const useChatStore = create<ChatState>((set, get) => ({
  // Initial State
  conversations: [],
  currentConversation: null,
  isLoadingConversations: false,
  messages: [],
  isStreaming: false,
  streamingContent: '',
  chatMode: 'assistant',
  selectedProvider: 'claude',
  isSidebarOpen: true,
  searchQuery: '',

  // Fetch Conversations
  fetchConversations: async (userId: string) => {
    set({ isLoadingConversations: true })
    try {
      const response = await api.get<{ data: Conversation[] }>('/chat', { params: { userId } })
      if (response.data && response.data.data) {
        set({ conversations: response.data.data })
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error)
    } finally {
      set({ isLoadingConversations: false })
    }
  },

  // Create Conversation
  createConversation: async (userId: string, title: string, mode: 'assistant' | 'agentic' | 'multi-agent') => {
    const response = await api.post<{ data: Conversation }>('/chat', { userId, title, mode })
    if (response.data && response.data.data) {
      set((state) => ({
        conversations: [response.data!.data, ...state.conversations],
        currentConversation: response.data!.data,
        messages: []
      }))
      return response.data!.data
    }
    throw new Error('Failed to create conversation')
  },

  // Select Conversation
  selectConversation: (conversation: Conversation) => {
    set({
      currentConversation: conversation,
      messages: conversation.messages || []
    })
  },

  // Delete Conversation
  deleteConversation: async (id: string) => {
    await api.delete(`/chat/${id}`)
    set((state) => ({
      conversations: state.conversations.filter((c: Conversation) => c.id !== id),
      currentConversation: state.currentConversation?.id === id ? null : state.currentConversation
    }))
  },

  // Send Message
  sendMessage: async (userId: string, content: string) => {
    const { currentConversation, messages, chatMode, selectedProvider, streamingContent } = get()
    
    // Add user message immediately
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date().toISOString()
    }
    
    set({
      messages: [...messages, userMessage],
      isStreaming: true,
      streamingContent: ''
    })

    try {
      // Call API with streaming
      const response = await makeStreamingRequest(
        {
          message: content,
          conversationId: currentConversation?.id,
          provider: selectedProvider,
          mode: chatMode
        },
        (chunk: string) => {
          set((state) => ({
            streamingContent: state.streamingContent + chunk
          }))
        }
      )

      // After streaming completes, add the AI message
      const aiMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: streamingContent || 'No response',
        timestamp: new Date().toISOString(),
        provider: selectedProvider
      }

      set((state) => ({
        messages: [...state.messages, aiMessage],
        isStreaming: false,
        streamingContent: ''
      }))

    } catch (error) {
      console.error('Failed to send message:', error)
      const errorMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: 'Sorry, something went wrong. Please try again.',
        timestamp: new Date().toISOString()
      }
      set((state) => ({
        messages: [...state.messages, errorMessage],
        isStreaming: false,
        streamingContent: ''
      }))
    }
  },

  // Cancel Streaming
  cancelStreaming: () => {
    set({ isStreaming: false, streamingContent: '' })
  },

  // UI Actions
  setChatMode: (mode: 'assistant' | 'agentic' | 'multi-agent') => {
    set({ chatMode: mode })
  },

  setSelectedProvider: (provider: string) => {
    set({ selectedProvider: provider })
  },

  toggleSidebar: () => {
    set((state) => ({ isSidebarOpen: !state.isSidebarOpen }))
  },

  setSearchQuery: (query: string) => {
    set({ searchQuery: query })
  },

  clearCurrentConversation: () => {
    set({
      currentConversation: null,
      messages: [],
      streamingContent: ''
    })
  }
}))
